class AnalyticsService {
  constructor() {
    this.clickEvents = JSON.parse(localStorage.getItem('clickEvents') || '[]');
    this.nextEventId = parseInt(localStorage.getItem('nextEventId') || '1');
  }

  saveToStorage() {
    localStorage.setItem('clickEvents', JSON.stringify(this.clickEvents));
    localStorage.setItem('nextEventId', this.nextEventId.toString());
  }

  // Generate demo data for visualization
  generateDemoData() {
    const demoEvents = [];
    const now = new Date();
    const countries = ['United States', 'Canada', 'United Kingdom', 'Germany', 'France', 'Australia', 'Japan', 'Brazil'];
    const devices = ['Desktop', 'Mobile', 'Tablet'];
    const browsers = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'];
    const referrers = ['google.com', 'twitter.com', 'facebook.com', 'linkedin.com', 'direct', 'email'];
    
    // Generate events for the last 30 days
    for (let i = 0; i < 1000; i++) {
      const daysAgo = Math.floor(Math.random() * 30);
      const hoursAgo = Math.floor(Math.random() * 24);
      const minutesAgo = Math.floor(Math.random() * 60);
      
      const eventTime = new Date(now);
      eventTime.setDate(eventTime.getDate() - daysAgo);
      eventTime.setHours(eventTime.getHours() - hoursAgo);
      eventTime.setMinutes(eventTime.getMinutes() - minutesAgo);

      const event = {
        id: this.nextEventId++,
        linkId: Math.floor(Math.random() * 10) + 1, // Assuming 10 demo links
        timestamp: eventTime.toISOString(),
        ip: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        country: countries[Math.floor(Math.random() * countries.length)],
        city: 'Demo City',
        device: devices[Math.floor(Math.random() * devices.length)],
        browser: browsers[Math.floor(Math.random() * browsers.length)],
        os: Math.random() > 0.5 ? 'Windows' : Math.random() > 0.5 ? 'macOS' : 'Linux',
        referrer: referrers[Math.floor(Math.random() * referrers.length)],
        userAgent: 'Demo User Agent',
        isUnique: Math.random() > 0.3, // 70% unique clicks
        isBot: Math.random() < 0.05, // 5% bot traffic
        utmSource: Math.random() > 0.7 ? 'campaign' : null,
        utmMedium: Math.random() > 0.7 ? 'social' : null,
        utmCampaign: Math.random() > 0.7 ? 'demo-campaign' : null
      };

      demoEvents.push(event);
    }

    this.clickEvents = [...this.clickEvents, ...demoEvents];
    this.saveToStorage();
  }

  // Initialize demo data if none exists
  initializeDemoData() {
    if (this.clickEvents.length === 0) {
      this.generateDemoData();
    }
  }

  recordClick(linkId, clickData = {}) {
    const event = {
      id: this.nextEventId++,
      linkId: parseInt(linkId),
      timestamp: new Date().toISOString(),
      ip: clickData.ip || '127.0.0.1',
      country: clickData.country || 'Unknown',
      city: clickData.city || 'Unknown',
      device: this.detectDevice(clickData.userAgent),
      browser: this.detectBrowser(clickData.userAgent),
      os: this.detectOS(clickData.userAgent),
      referrer: clickData.referrer || 'direct',
      userAgent: clickData.userAgent || navigator.userAgent,
      isUnique: clickData.isUnique || false,
      isBot: this.detectBot(clickData.userAgent),
      utmSource: clickData.utmSource || null,
      utmMedium: clickData.utmMedium || null,
      utmCampaign: clickData.utmCampaign || null
    };

    this.clickEvents.push(event);
    this.saveToStorage();
    return event;
  }

  detectDevice(userAgent = '') {
    const ua = userAgent.toLowerCase();
    if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) {
      return 'Mobile';
    }
    if (ua.includes('tablet') || ua.includes('ipad')) {
      return 'Tablet';
    }
    return 'Desktop';
  }

  detectBrowser(userAgent = '') {
    const ua = userAgent.toLowerCase();
    if (ua.includes('chrome')) return 'Chrome';
    if (ua.includes('firefox')) return 'Firefox';
    if (ua.includes('safari') && !ua.includes('chrome')) return 'Safari';
    if (ua.includes('edge')) return 'Edge';
    if (ua.includes('opera')) return 'Opera';
    return 'Other';
  }

  detectOS(userAgent = '') {
    const ua = userAgent.toLowerCase();
    if (ua.includes('windows')) return 'Windows';
    if (ua.includes('macintosh') || ua.includes('mac os')) return 'macOS';
    if (ua.includes('linux')) return 'Linux';
    if (ua.includes('android')) return 'Android';
    if (ua.includes('ios') || ua.includes('iphone') || ua.includes('ipad')) return 'iOS';
    return 'Other';
  }

  detectBot(userAgent = '') {
    const ua = userAgent.toLowerCase();
    const botPatterns = ['bot', 'crawler', 'spider', 'scraper', 'facebook', 'twitter'];
    return botPatterns.some(pattern => ua.includes(pattern));
  }

  // Get overall analytics for a user
  getUserAnalytics(userId, dateRange = 30) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dateRange);

    const userLinks = JSON.parse(localStorage.getItem('links') || '[]')
      .filter(link => link.createdBy === userId);
    
    const linkIds = userLinks.map(link => link.id);
    
    const events = this.clickEvents.filter(event => 
      linkIds.includes(event.linkId) &&
      new Date(event.timestamp) >= startDate &&
      new Date(event.timestamp) <= endDate &&
      !event.isBot
    );

    return {
      totalClicks: events.length,
      uniqueClicks: events.filter(e => e.isUnique).length,
      totalLinks: userLinks.length,
      activeLinks: userLinks.filter(l => l.isActive).length,
      topPerformingLinks: this.getTopLinks(linkIds, events, 5),
      clicksByDate: this.getClicksByDate(events, dateRange),
      clicksByCountry: this.getClicksByCountry(events),
      clicksByDevice: this.getClicksByDevice(events),
      clicksByBrowser: this.getClicksByBrowser(events),
      clicksByReferrer: this.getClicksByReferrer(events),
      recentActivity: this.getRecentActivity(events, 10)
    };
  }

  // Get analytics for a specific link
  getLinkAnalytics(linkId, dateRange = 30) {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - dateRange);

    const events = this.clickEvents.filter(event => 
      event.linkId === parseInt(linkId) &&
      new Date(event.timestamp) >= startDate &&
      new Date(event.timestamp) <= endDate &&
      !event.isBot
    );

    return {
      totalClicks: events.length,
      uniqueClicks: events.filter(e => e.isUnique).length,
      clicksByDate: this.getClicksByDate(events, dateRange),
      clicksByHour: this.getClicksByHour(events),
      clicksByCountry: this.getClicksByCountry(events),
      clicksByDevice: this.getClicksByDevice(events),
      clicksByBrowser: this.getClicksByBrowser(events),
      clicksByOS: this.getClicksByOS(events),
      clicksByReferrer: this.getClicksByReferrer(events),
      utmAnalytics: this.getUTMAnalytics(events),
      recentClicks: events.slice(-20).reverse()
    };
  }

  getTopLinks(linkIds, events, limit = 5) {
    const linkCounts = {};
    events.forEach(event => {
      linkCounts[event.linkId] = (linkCounts[event.linkId] || 0) + 1;
    });

    const links = JSON.parse(localStorage.getItem('links') || '[]');
    
    return Object.entries(linkCounts)
      .sort(([,a], [,b]) => b - a)
      .slice(0, limit)
      .map(([linkId, clicks]) => {
        const link = links.find(l => l.id === parseInt(linkId));
        return {
          link,
          clicks,
          uniqueClicks: events.filter(e => e.linkId === parseInt(linkId) && e.isUnique).length
        };
      });
  }

  getClicksByDate(events, dateRange) {
    const dateMap = {};
    const endDate = new Date();
    
    // Initialize all dates in range
    for (let i = 0; i < dateRange; i++) {
      const date = new Date(endDate);
      date.setDate(date.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      dateMap[dateKey] = { date: dateKey, clicks: 0, uniqueClicks: 0 };
    }

    // Count clicks by date
    events.forEach(event => {
      const dateKey = event.timestamp.split('T')[0];
      if (dateMap[dateKey]) {
        dateMap[dateKey].clicks++;
        if (event.isUnique) {
          dateMap[dateKey].uniqueClicks++;
        }
      }
    });

    return Object.values(dateMap).reverse();
  }

  getClicksByHour(events) {
    const hourMap = {};
    
    // Initialize all hours
    for (let i = 0; i < 24; i++) {
      hourMap[i] = { hour: i, clicks: 0 };
    }

    // Count clicks by hour
    events.forEach(event => {
      const hour = new Date(event.timestamp).getHours();
      hourMap[hour].clicks++;
    });

    return Object.values(hourMap);
  }

  getClicksByCountry(events) {
    const countryMap = {};
    
    events.forEach(event => {
      countryMap[event.country] = (countryMap[event.country] || 0) + 1;
    });

    return Object.entries(countryMap)
      .map(([country, clicks]) => ({ country, clicks }))
      .sort((a, b) => b.clicks - a.clicks);
  }

  getClicksByDevice(events) {
    const deviceMap = {};
    
    events.forEach(event => {
      deviceMap[event.device] = (deviceMap[event.device] || 0) + 1;
    });

    return Object.entries(deviceMap)
      .map(([device, clicks]) => ({ device, clicks, percentage: 0 }))
      .sort((a, b) => b.clicks - a.clicks)
      .map(item => ({
        ...item,
        percentage: events.length > 0 ? Math.round((item.clicks / events.length) * 100) : 0
      }));
  }

  getClicksByBrowser(events) {
    const browserMap = {};
    
    events.forEach(event => {
      browserMap[event.browser] = (browserMap[event.browser] || 0) + 1;
    });

    return Object.entries(browserMap)
      .map(([browser, clicks]) => ({ browser, clicks }))
      .sort((a, b) => b.clicks - a.clicks);
  }

  getClicksByOS(events) {
    const osMap = {};
    
    events.forEach(event => {
      osMap[event.os] = (osMap[event.os] || 0) + 1;
    });

    return Object.entries(osMap)
      .map(([os, clicks]) => ({ os, clicks }))
      .sort((a, b) => b.clicks - a.clicks);
  }

  getClicksByReferrer(events) {
    const referrerMap = {};
    
    events.forEach(event => {
      let referrer = event.referrer || 'direct';
      if (referrer.startsWith('http')) {
        try {
          referrer = new URL(referrer).hostname;
        } catch (e) {
          referrer = 'unknown';
        }
      }
      referrerMap[referrer] = (referrerMap[referrer] || 0) + 1;
    });

    return Object.entries(referrerMap)
      .map(([referrer, clicks]) => ({ referrer, clicks }))
      .sort((a, b) => b.clicks - a.clicks);
  }

  getUTMAnalytics(events) {
    const utmEvents = events.filter(e => e.utmSource || e.utmMedium || e.utmCampaign);
    
    return {
      sources: this.groupBy(utmEvents, 'utmSource'),
      mediums: this.groupBy(utmEvents, 'utmMedium'),
      campaigns: this.groupBy(utmEvents, 'utmCampaign')
    };
  }

  groupBy(events, field) {
    const grouped = {};
    events.forEach(event => {
      const value = event[field] || 'unknown';
      grouped[value] = (grouped[value] || 0) + 1;
    });
    
    return Object.entries(grouped)
      .map(([name, clicks]) => ({ name, clicks }))
      .sort((a, b) => b.clicks - a.clicks);
  }

  getRecentActivity(events, limit = 10) {
    return events
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
      .slice(0, limit);
  }

  // Export analytics data
  exportAnalytics(userId, format = 'csv', dateRange = 30) {
    const analytics = this.getUserAnalytics(userId, dateRange);
    
    if (format === 'csv') {
      return this.generateCSV(analytics);
    }
    
    if (format === 'json') {
      return JSON.stringify(analytics, null, 2);
    }
    
    return analytics;
  }

  generateCSV(analytics) {
    const csvRows = [];
    
    // Add summary data
    csvRows.push(['Summary']);
    csvRows.push(['Total Clicks', analytics.totalClicks]);
    csvRows.push(['Unique Clicks', analytics.uniqueClicks]);
    csvRows.push(['Total Links', analytics.totalLinks]);
    csvRows.push(['Active Links', analytics.activeLinks]);
    csvRows.push(['']);
    
    // Add daily data
    csvRows.push(['Daily Analytics']);
    csvRows.push(['Date', 'Clicks', 'Unique Clicks']);
    analytics.clicksByDate.forEach(day => {
      csvRows.push([day.date, day.clicks, day.uniqueClicks]);
    });
    csvRows.push(['']);
    
    // Add country data
    csvRows.push(['Country Analytics']);
    csvRows.push(['Country', 'Clicks']);
    analytics.clicksByCountry.forEach(country => {
      csvRows.push([country.country, country.clicks]);
    });
    
    return csvRows.map(row => row.join(',')).join('\n');
  }

  // Performance metrics
  getPerformanceMetrics(userId, dateRange = 30) {
    const analytics = this.getUserAnalytics(userId, dateRange);
    const previousPeriodAnalytics = this.getUserAnalytics(userId, dateRange, dateRange);
    
    const clicksGrowth = this.calculateGrowth(
      analytics.totalClicks, 
      previousPeriodAnalytics.totalClicks
    );
    
    const uniqueClicksGrowth = this.calculateGrowth(
      analytics.uniqueClicks, 
      previousPeriodAnalytics.uniqueClicks
    );
    
    return {
      clicksGrowth,
      uniqueClicksGrowth,
      averageClicksPerLink: analytics.totalLinks > 0 ? 
        Math.round(analytics.totalClicks / analytics.totalLinks) : 0,
      clickThroughRate: analytics.totalClicks > 0 ? 
        Math.round((analytics.uniqueClicks / analytics.totalClicks) * 100) : 0,
      topPerformingDay: this.getTopPerformingDay(analytics.clicksByDate),
      engagement: this.calculateEngagement(analytics)
    };
  }

  calculateGrowth(current, previous) {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  }

  getTopPerformingDay(dailyData) {
    return dailyData.reduce((max, day) => 
      day.clicks > max.clicks ? day : max, 
      { clicks: 0, date: null }
    );
  }

  calculateEngagement(analytics) {
    const totalInteractions = analytics.totalClicks;
    const uniqueUsers = analytics.uniqueClicks;
    
    if (uniqueUsers === 0) return 0;
    return Math.round((totalInteractions / uniqueUsers) * 100) / 100;
  }
}

export const analyticsService = new AnalyticsService();